<template>
  <b-card v-if="!!option" id="page-company-form" no-body class="mb-0">
    <b-card-header>
      <b-card-title
        ><h2>
          {{ $t(getTitle()) }} {{ $t("pages.profiles.title").toLowerCase() }}
        </h2></b-card-title
      >
      <b-button variant="primary" @click="goBack">
        <feather-icon icon="ArrowLeftIcon" />
        {{ $t("back") }}
      </b-button>
    </b-card-header>

    <b-card-body>
      <validation-observer ref="simpleRules">
        <b-form>
          <b-row>
            <b-col cols="12" sm="12" md="6">
              <b-form-group :label="$t(label('name'))" label-for="name">
                <validation-provider
                  #default="{ errors }"
                  :name="$t(label('name'))"
                  rules="required"
                >
                  <b-form-input
                    id="name"
                    v-model="profile.name"
                    :state="errors.length > 0 ? false : null"
                    :placeholder="$t(label('name'))"
                    :disabled="!isSaveMode"
                  />
                  <small class="text-danger">{{ errors[0] }}</small>
                </validation-provider>
              </b-form-group>
            </b-col>

            <b-col cols="12" sm="12" md="6">
              <b-form-group
                :label="$t(label('typeProfile'))"
                label-for="typeProfile"
              >
                <validation-provider
                  #default="{ errors }"
                  :name="$t(label('typeProfile'))"
                  rules="required"
                >
                  <v-select
                    input-id="typeProfile"
                    v-model="profile.idCatType"
                    :options="listCatTypeProfiles"
                    label="name"
                    :reduce="(option) => option.id"
                    :placeholder="$t(label('typeProfile'))"
                    :disabled="
                      !isSaveMode || !isBackoffice || !!activeCompanyGuid
                    "
                  >
                    <span slot="no-options">{{ $t("sorryOption") }}</span>
                  </v-select>
                  <small class="text-danger">{{ errors[0] }}</small>
                </validation-provider>
              </b-form-group>
            </b-col>

            <b-col cols="12" sm="12" md="6">
              <b-form-group
                :label="$t(label('permission'))"
                label-for="permission"
              >
                <validation-provider
                  #default="{ errors }"
                  :name="$t(label('permission'))"
                  rules="required"
                >
                  <v-select
                    input-id="permission"
                    ref="permission"
                    v-model="profile.permissions"
                    :options="allPermission"
                    label="name"
                    :placeholder="$t(label('permission'))"
                    :disabled="
                      !isSaveMode ||
                      !profile.idCatType ||
                      isLoadingPermissions ||
                      needsCompanySelection
                    "
                    multiple
                    :reduce="(option) => option"
                  >
                    <span slot="no-options">{{ $t("sorryOption") }}</span>
                  </v-select>
                  <small class="text-danger">{{ errors[0] }}</small>
                </validation-provider>
              </b-form-group>
            </b-col>

            <b-col v-if="needsCompanySelection" cols="12">
              <b-alert show variant="warning" class="mb-2">
                <div class="d-flex align-items-center">
                  <feather-icon icon="AlertTriangleIcon" size="18" />
                  <span class="ml-1">
                    {{ $t("pages.profiles.form.selectClientCompany") }}
                  </span>
                </div>
              </b-alert>
            </b-col>

            <b-col
              class="d-flex justify-content-end align-items-center"
              cols="12"
            >
              <b-button
                v-if="option != 2"
                v-ripple.400="'rgba(255, 255, 255, 0.15)'"
                type="submit"
                variant="primary"
                class="mr-1"
                :disabled="needsCompanySelection"
                @click.prevent="onSubmit"
              >
                {{ $t("save") }}
              </b-button>
              <b-button
                v-if="option == 1"
                v-ripple.400="'rgba(186, 191, 199, 0.15)'"
                type="reset"
                variant="outline-secondary"
                @click="onCancel"
              >
                {{ $t("cancel") }}
              </b-button>
            </b-col>
          </b-row>
        </b-form>
      </validation-observer>
    </b-card-body>
  </b-card>
</template>

<script>
import _ from "lodash";
import { ValidationProvider, ValidationObserver, localize } from "vee-validate";
import {
  BCard,
  BCardHeader,
  BCardTitle,
  BCardBody,
  BRow,
  BCol,
  BFormInput,
  BButton,
  BTable,
  BMedia,
  BAvatar,
  BLink,
  BBadge,
  BDropdown,
  BDropdownItem,
  BPagination,
  BFormGroup,
  BFormCheckbox,
  BForm,
  BFormTextarea,
  BAlert,
  BFormSelect,
  BModal,
  BToast,
  BFormRadio,
  BFormRadioGroup,
  BFormInvalidFeedback,
} from "bootstrap-vue";
import router from "@/router";
import Ripple from "vue-ripple-directive";
import { required, email } from "@validations";
import vSelect from "vue-select";
import { createOrEditProfile, getProfile } from "@/@core/api/request/profiles";
import { getCompany } from "@core/api/request/company";
import { allPermissions } from "@core/api/request/permissions";
import { getCatalog } from "@core/api/request/catalogs";

import { getParams, showLoading, stopLoading } from "@core/utils/utils";
import { VueTelInput } from "vue-tel-input";
import "vue-tel-input/dist/vue-tel-input.css";

const profile = {
  guid: null,
  name: null,
  idCatType: null,
  idCompany: null,
  permissions: [],
};

const ADMIN_PROFILE_TYPE_ID = 314;
const CLIENT_PROFILE_TYPE_ID = 315;

export default {
  components: {
    BFormRadio,
    BFormRadioGroup,
    BAlert,
    BModal,
    ValidationProvider,
    ValidationObserver,
    BCard,
    BCardHeader,
    BCardTitle,
    BCardBody,
    BRow,
    BCol,
    BFormInput,
    BButton,
    BTable,
    BMedia,
    BAvatar,
    BLink,
    BBadge,
    BDropdown,
    BDropdownItem,
    BPagination,
    BFormGroup,
    BFormInput,
    BFormCheckbox,
    BForm,
    vSelect,
    BFormTextarea,
    BFormSelect,
    BToast,
    BFormInvalidFeedback,
    VueTelInput,
  },
  directives: {
    Ripple,
  },
  data: () => ({
    openTelInput: false,
    isSaveMode: true,
    required,
    email,
    option: "",
    profile: Object.assign(profile),
    guidCatalogTypeProfile: "953d6a85-82c4-4d02-abb2-13c9b5baa3ae",
    profileTypeCatalog: [],
    listCatTypeProfiles: [],
    allPermission: [],
    idCompany: null,
    isLoadingPermissions: false,
    isHydratingProfile: false,
    permissionsRequestId: 0,
  }),

  computed: {
    isBackoffice() {
      const role =
        this.$store.state.appConfig.role ||
        this.$store.state.appConfig.sessionContext?.profileName;
      return (
        String(role || "")
          .trim()
          .toUpperCase()
          .replace(/\s/g, "") === "BACKOFFICE"
      );
    },
    activeCompanyGuid() {
      return this.$store.state.appConfig.activeCompanyGuid;
    },
    profileTypeForContext() {
      return this.isBackoffice && !this.activeCompanyGuid
        ? ADMIN_PROFILE_TYPE_ID
        : CLIENT_PROFILE_TYPE_ID;
    },
    isClientProfile() {
      return Number(this.profile.idCatType) === CLIENT_PROFILE_TYPE_ID;
    },
    needsCompanySelection() {
      return (
        this.isClientProfile &&
        (!this.activeCompanyGuid || !this.normalizeCompanyId(this.idCompany))
      );
    },
  },

  created() {
    localize("es");
    this.initialize();
  },

  watch: {
    async "$store.state.appConfig.activeCompanyGuid"() {
      const previousProfileType = Number(this.profile.idCatType);
      this.idCompany = null;
      this.profile.idCompany = null;
      this.syncProfileTypeWithContext();

      if (!this.isHydratingProfile) {
        this.allPermission = [];
        this.profile.permissions = [];
        if (previousProfileType === this.profileTypeForContext) {
          await this.loadPermissionsByType(this.profileTypeForContext);
        }
      }
    },
    "$store.state.appConfig.idCompany"(idCompany) {
      this.idCompany = this.normalizeCompanyId(idCompany);
      this.profile.idCompany = this.idCompany;
    },
    "$store.state.appConfig.idCatTypeProfile"() {
      this.syncProfileTypeWithContext();
    },

    async "profile.idCatType"(newIdCatType, oldIdCatType) {
      if (
        !newIdCatType ||
        newIdCatType === oldIdCatType ||
        this.isHydratingProfile
      )
        return;
      this.profile.permissions = [];
      await this.loadPermissionsByType(newIdCatType);
    },
  },

  mounted() {},

  methods: {
    label(field) {
      return `pages.profiles.form.label.${field}`;
    },

    getTitle() {
      if (this.option === 1) return "register";
      else if (this.option === 2) return "show";
      else if (this.option === 3) return "edit";
    },

    async initialize() {
      this.option = this.$route.params.option;
      if (this.option === undefined) {
        router.push({ name: "profiles" }).catch(() => {});
      }

      switch (this.option) {
        case 1:
          this.onCancel();
          this.idCompany = this.normalizeCompanyId(
            this.$store.getters["appConfig/activeCompanyId"],
          );
          this.profile.idCompany = this.idCompany;
          await this.loadCatData();
          break;
        case 2:
        case 3:
          if (this.$route.params.guid === undefined) {
            router.push({ name: "profiles" }).catch(() => {});
          }
          this.isSaveMode = this.option !== 2;
          this.idCompany = this.normalizeCompanyId(
            this.$store.getters["appConfig/activeCompanyId"],
          );
          await this.loadCatData();
          await this.getProfile(this.$route.params.guid);
          break;
      }
    },

    onSubmit() {
      if (this.needsCompanySelection) {
        this.alertToast(
          "warning",
          this.$t("Alert"),
          this.$t("pages.profiles.form.selectClientCompany"),
          "AlertTriangleIcon",
        );
        return;
      }

      this.$refs.simpleRules.validate().then((success) => {
        if (success) {
          showLoading();
          const data = {
            guid: this.profile.guid,
            name: this.profile.name,
            idCatType: this.profileTypeForContext,
            idCompany: this.profile.idCompany,
            permissions: this.profile.permissions,
          };
          const guidProfile = this.option === 1 ? null : this.profile.guid;
          createOrEditProfile(data, guidProfile)
            .then(() => {
              this.goBack();
            })
            .catch((error) => {
              console.log("error", error.message);
              this.alertToast(
                "danger",
                "Error",
                error.message,
                "AlertTriangleIcon",
              );
            })
            .finally(() => {
              stopLoading();
            });
        }
      });
    },

    normalizeCompanyId(value) {
      const idCompany = Number(value);
      return Number.isInteger(idCompany) && idCompany > 0 ? idCompany : null;
    },

    syncProfileTypeWithContext() {
      const idCatType = this.profileTypeForContext;
      this.listCatTypeProfiles = this.profileTypeCatalog.filter(
        (item) => Number(item.id) === idCatType,
      );
      this.profile.idCatType = idCatType;
    },

    getProfile(guidProfile) {
      showLoading();
      this.isHydratingProfile = true;
      getProfile(guidProfile)
        .then(async (resp) => {
          const response = _.get(resp, "data");
          console.log("response", response);
          this.profile = Object.assign(this.profile, {
            guid: response.guid,
            name: response.name,
            idCatType: this.profileTypeForContext,
            idCompany: response.idCompany,
            permissions: Array.isArray(response.permissions)
              ? response.permissions
              : [],
          });
          if (this.profile.idCatType) {
            await this.loadPermissionsByType(this.profile.idCatType, true);
          }
          console.log("profile", this.profile);
        })
        .catch((error) => {
          console.log("error", error);
          this.alertToast(
            "danger",
            "Error",
            error.message,
            "AlertTriangleIcon",
          );
        })
        .finally(() => {
          this.isHydratingProfile = false;
          stopLoading();
        });
    },

    async loadCatData() {
      showLoading();
      try {
        // TYPE_PROFILE
        const arrParamTypeProfile = [];

        arrParamTypeProfile.push(`itemsPage,1`);
        arrParamTypeProfile.push(`itemsRows,999`);
        arrParamTypeProfile.push(`guidCatalog,${this.guidCatalogTypeProfile}`);

        const paramTypeProfile = getParams(arrParamTypeProfile);

        // PERMISOS_MODULOS
        // const arrParamPermissions = [];

        // arrParamPermissions.push(`page,1`);
        // arrParamPermissions.push(`rows,999`);

        // const paramPermissions = getParams(arrParamPermissions);

        const [resultTypeProfile] = await Promise.all([
          getCatalog(paramTypeProfile),
          // allPermissions(paramPermissions),
        ]);

        this.profileTypeCatalog =
          _.get(resultTypeProfile, "data.listOfItems.items") || [];
        this.syncProfileTypeWithContext();

        // this.allPermission = _.get(resultPermission, "data") || [];
        stopLoading();
      } catch (error) {
        stopLoading();
        this.alertToast(
          "danger",
          this.$t("Alert"),
          error.message,
          "AlertTriangleIcon",
        );
        console.error("Error loading data", error.message);
      }
    },

    async loadPermissionsByType(idCatType, keepSelection = false) {
      const requestId = ++this.permissionsRequestId;

      if (!idCatType) {
        this.allPermission = [];
        if (!keepSelection) {
          this.profile.permissions = [];
        } else {
          const selectedKeys = new Set(
            (this.profile.permissions || []).map(
              (p) => p.idPermission ?? p.guid,
            ),
          );

          this.profile.permissions = this.allPermission.filter((p) =>
            selectedKeys.has(p.idPermission ?? p.guid),
          );
        }
        return;
      }

      if (Number(idCatType) === 315 && !this.activeCompanyGuid) {
        this.allPermission = [];
        this.profile.permissions = [];
        this.isLoadingPermissions = false;
        return;
      }

      this.isLoadingPermissions = true;
      try {
        const requestedCompanyGuid = this.activeCompanyGuid;
        const arrParams = [];
        arrParams.push("page,1");
        arrParams.push("rows,1000");
        arrParams.push(`idCatType,${idCatType}`);

        if (Number(idCatType) === 315) {
          arrParams.push(`guidCompany,${this.activeCompanyGuid}`);
        }

        const param = getParams(arrParams);
        const resp = await allPermissions(param);

        if (
          requestId !== this.permissionsRequestId ||
          Number(idCatType) !== Number(this.profile.idCatType) ||
          (Number(idCatType) === 315 &&
            requestedCompanyGuid !== this.activeCompanyGuid)
        ) {
          return;
        }

        this.allPermission = _.get(resp, "data", []) || [];

        if (!keepSelection) {
          this.profile.permissions = [];
        } else {
          const selectedIds = new Set(
            (this.profile.permissions || []).map((p) => p.idPermission),
          );
          this.profile.permissions = this.allPermission.filter((p) =>
            selectedIds.has(p.idPermission),
          );
        }
      } catch (error) {
        if (requestId !== this.permissionsRequestId) return;
        this.allPermission = [];
        if (!keepSelection) this.profile.permissions = [];
        this.alertToast("danger", "Error", error.message, "AlertTriangleIcon");
      } finally {
        if (requestId === this.permissionsRequestId) {
          this.isLoadingPermissions = false;
        }
      }
    },

    goBack() {
      this.$refs.simpleRules.reset();
      router.back();
      this.onCancel();
    },

    onCancel() {
      Object.assign(this.profile, {
        guid: null,
        name: null,
        idCatType: this.profileTypeForContext,
        idCompany: null,
        permissions: [],
      });
      this.listCatTypeProfiles = [];
      this.profileTypeCatalog = [];
      this.allPermission = [];
      this.isLoadingPermissions = false;
      this.isHydratingProfile = false;
      this.permissionsRequestId += 1;
    },

    alertToast(type = null, title = null, message = null, icon) {
      this.$bvToast.toast(message, {
        title,
        variant: type,
        solid: true,
        icon,
      });
    },
  },
};
</script>

<style lang="scss">
@import "@core/scss/vue/libs/vue-select.scss";
</style>
